<template>
    <div class="players">
        <show-import-placeholder
            v-if="[ImportStatus.Pending, ImportStatus.Error].includes(show.importStatus)"
            :import-status="show.importStatus"
        />

        <ShowFrozenPlaceholder
            v-else-if="show.isFrozen"
            :show="show"
        />

        <template v-else>
            <transition
                name="fade"
                mode="out-in"
            >
                <base-loader v-if="!show" key="loader" />
                <div v-else>
                    <upgrade-message-box
                        key="upgrade-message-box"
                        :show="show"
                    />
                    <archivePlayers
                        v-if="hasArchivePlayers && null !== showArchivePlayers"
                        key="archivePlayers"
                        :players="showArchivePlayers"
                        :show="show"
                        @click="controlsChange"
                    />
                    <singleTrackPlayers
                        v-if="null !== showTrackPlayers"
                        key="trackPlayers"
                        :players="showTrackPlayers"
                        :show="show"
                        :player-loading="singleTrackPlayerLoading"
                        :pagination="trackPlayersPaginationInfo"
                        :is-page-loading="isCurrentPageLoading"
                        @click="controlsChange"
                        @page-change="handlePageChange"
                    />
                </div>
            </transition>
            <transition
                name="fade"
            >
                <player-settings-modal
                    v-if="editPlayerType"
                    :is-track-featured="featured"
                    :player-type="editPlayerType"
                    :player="player"
                    :show-embed="showEmbed"
                    :show="show"
                    :track-guid="trackGuid"
                    :track-no="trackNo"
                    :track-player-name="title"
                    @close="controlsChange"
                    @update="controlsChange"
                />
            </transition>
        </template>
    </div>
</template>

<script>
import {
    mapActions,
    mapState,
    mapMutations,
} from 'vuex';
import ArchivePlayers from '@/components/Players/ArchivePlayers';
import dataChangeHandler from '@/utils/dataChangeHandler';
import { Feature, hasFeature, ImportStatus } from '@/utils/show';
import PlayerSettingsModal from '@/components/Players/SettingsModal/PlayerSettingsModal';
import ShowImportPlaceholder from '@/components/Shows/ShowImportPlaceholder';
import { ShowFrozenPlaceholder } from '@/components/Shows';
import SingleTrackPlayers from '@/components/Players/SingleTrackPlayers';
import toasts from '@/mixins/toasts';
import UpgradeMessageBox from '@/components/Shows/UpgradeMessageBox';

export default {
    components: {
        ArchivePlayers,
        PlayerSettingsModal,
        ShowFrozenPlaceholder,
        ShowImportPlaceholder,
        SingleTrackPlayers,
        UpgradeMessageBox,
    },

    mixins: [toasts],

    props: {
        show: {
            type: [Object, Boolean],
            default: () => ({}),
        },
    },

    data: () => ({
        editPlayerType: null,
        player: null,
        showEmbed: false,
        trackNo: null,
        trackGuid: null,
        featured: false,
        title: '',
        singleTrackPlayerLoading: false,
        ImportStatus,
    }),

    computed: {
        ...mapState([
            'archivePlayers',
            'trackPlayers',
            'trackPlayersConfig',
            'trackPlayersPagination',
        ]),

        /**
         * Returns archive players
         *
         * @returns  {Array}  Archive players
         */
        showArchivePlayers() {
            return this.getPlayers('archive');
        },

        /**
         * Checks if the show has archive players feature enabled.
         *
         * @returns {boolean} True if the show has archive players feature.
         */
        hasArchivePlayers() {
            return hasFeature(this.show, Feature.ArchivePlayer);
        },

        /**
         * Returns current page data
         *
         * @returns  {*}  Current page data
         */
        currentPageData() {
            const currentPage = this.trackPlayersPaginationInfo?.currentPage || 1;

            return this.trackPlayers?.[this.show.hash]?.find(p => p.page === currentPage);
        },

        /**
         * Returns  track players
         *
         * @returns  {Array}  Track players
         */
        showTrackPlayers() {
            return this.currentPageData?.players
                ? Object.values(this.currentPageData.players)
                : false;
        },

        /**
         * Returns pagination info for track players
         *
         * @returns  {object|null}  Pagination metadata
         */
        trackPlayersPaginationInfo() {
            return this.trackPlayersPagination?.[this.show.hash] || null;
        },

        /**
         * Returns loading state for current page
         *
         * @returns  {boolean}  Whether current page is loading
         */
        isCurrentPageLoading() {
            return !! this.currentPageData?.loading;
        },

        /**
         * Returns track player config
         *
         * @returns  {*}  Undefined/Player config
         */
        trackPlayerConfig() {
            return this.trackPlayersConfig?.[this.show.hash]?.[this.trackNo];
        },
    },

    /**
     * Created hook
     */
    async created() {
        const archivePlayers = this.loadPlayers({ type: 'archive', showHash: this.show.hash });
        const trackPlayers = this.loadPlayers({ type: 'track', showHash: this.show.hash });

        await Promise.all([archivePlayers, trackPlayers]);

        if (this.$route.query?.edit) {
            const { type, player, showEmbed } = this.$route.query;

            this.controlsChange({
                editPlayerType: type,
                showEmbed: !! showEmbed,
                player: 'archive' === type
                    ? this.archivePlayers[this.show.hash][player]
                    : player,
            });
        }
    },

    methods: {
        ...mapActions([
            'loadPlayers',
            'loadTrackPlayerConfig',
        ]),

        ...mapMutations(['changeUIState']),

        /**
         * Get players of given type
         *
         * @param {string} type  Players type
         * @returns {*} Array of players/false
         */
        getPlayers(type) {
            const players = this[`${type}Players`]?.[this.show.hash];

            return players ? Object.values(players) : false;
        },

        /**
         * Loads player config
         *
         * @param {number} trackNo  Track number
         * @returns {boolean} True if config exists or was successfully loaded.
         */
        async getTrackPlayerConfig({ trackNo, trackGuid, featured, title }) {
            this.trackNo = trackNo;
            this.trackGuid = trackGuid;
            this.featured = featured;
            this.title = title;

            if (this.trackPlayerConfig) {
                return true;
            }

            try {
                this.singleTrackPlayerLoading = ! this.singleTrackPlayerLoading;
                await this.loadTrackPlayerConfig({ showHash: this.show.hash, trackNo });
                this.singleTrackPlayerLoading = ! this.singleTrackPlayerLoading;
            } catch (e) {
                this.toastError({
                    message: {
                        title: 'We couldn\'t load player config. Please try again later or contact us.',
                    },
                });

                return false;
            }

            return true;
        },

        /**
         * Handle data change
         *
         * @param {object} config  Object with data configuration
         */
        async controlsChange(config) {
            if ('track' === config.editPlayerType) {
                await this.getTrackPlayerConfig(config.track);

                config.player = this.trackPlayerConfig;
            }

            dataChangeHandler(this, config);
        },

        /**
         * Handle page change for track players
         *
         * @param {number} page  Page number
         */
        async handlePageChange(page) {
            try {
                await this.loadPlayers({
                    type: 'track',
                    showHash: this.show.hash,
                    page,
                    limit: this.trackPlayersPaginationInfo?.perPage || 10,
                    force: false, // Don't force reload, use cache if available
                });
            } catch (e) {
                this.toastError({
                    message: {
                        title: 'We couldn\'t load episodes. Please try again later or contact us.',
                    },
                });
            }
        },
    },
};
</script>
